/**
 * Async wrapper around the crypto Web Worker for off-main-thread file
 * encryption and decryption.
 *
 * - Lazily creates the worker on first use.
 * - Falls back to the synchronous shared-package implementation when Web
 *   Workers are unavailable (e.g. SSR, older browsers).
 * - Skips the worker for files <= 1 MB -- the overhead of serialising to/from
 *   the worker outweighs the benefit for small payloads.
 */
import {
  encryptFileBytes as encryptFileBytesSync,
  decryptFileBytes as decryptFileBytesSync,
} from '@clinikchat/shared';
import type { CryptoWorkerRequest, CryptoWorkerResponse } from '../workers/crypto.worker';

/** 1 MB threshold -- files at or below this size run synchronously. */
const WORKER_THRESHOLD = 1024 * 1024;

let worker: Worker | null = null;
let nextId = 0;
const pending = new Map<
  number,
  { resolve: (v: CryptoWorkerResponse) => void; reject: (e: Error) => void }
>();

function supportsWorkers(): boolean {
  return typeof Worker !== 'undefined';
}

function getWorker(): Worker | null {
  if (worker) return worker;
  if (!supportsWorkers()) return null;

  try {
    worker = new Worker(new URL('../workers/crypto.worker.ts', import.meta.url), {
      type: 'module',
    });

    worker.onmessage = (e: MessageEvent<CryptoWorkerResponse>) => {
      const { id } = e.data;
      const entry = pending.get(id);
      if (entry) {
        pending.delete(id);
        entry.resolve(e.data);
      }
    };

    worker.onerror = (err) => {
      // Reject all pending requests and dispose the broken worker.
      const error = new Error(`Crypto worker error: ${err.message}`);
      for (const [, entry] of pending) {
        entry.reject(error);
      }
      pending.clear();
      worker?.terminate();
      worker = null;
    };

    return worker;
  } catch {
    // Worker instantiation can fail (e.g. CSP restrictions).
    worker = null;
    return null;
  }
}

function postToWorker(
  msg: Omit<CryptoWorkerRequest, 'id'>,
  transfer?: Transferable[],
): Promise<CryptoWorkerResponse> {
  const w = getWorker();
  if (!w) return Promise.reject(new Error('Worker unavailable'));

  const id = nextId++;
  return new Promise<CryptoWorkerResponse>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    const request: CryptoWorkerRequest = { id, ...msg };
    if (transfer?.length) {
      w.postMessage(request, transfer);
    } else {
      w.postMessage(request);
    }
  });
}

/**
 * Encrypt raw file bytes with a symmetric channel key.
 * Returns base64(nonce[24] + ciphertext).
 *
 * For files > 1 MB the encryption runs in a Web Worker so the main thread
 * stays responsive. Smaller files and environments without Web Workers use
 * the synchronous implementation.
 */
export async function encryptFileBytesAsync(
  data: Uint8Array,
  channelKeyB64: string,
): Promise<string> {
  if (data.length <= WORKER_THRESHOLD || !supportsWorkers()) {
    return encryptFileBytesSync(data, channelKeyB64);
  }

  // Copy so the transfer doesn't detach the caller's buffer.
  const dataCopy = new Uint8Array(data);

  try {
    const response = await postToWorker({ type: 'encrypt', payload: dataCopy, channelKeyB64 }, [
      dataCopy.buffer,
    ]);
    if (response.error || response.result === null) {
      throw new Error(response.error ?? 'Encryption failed in worker');
    }
    return response.result as string;
  } catch {
    // Fallback to synchronous on any worker failure.
    return encryptFileBytesSync(data, channelKeyB64);
  }
}

/**
 * Decrypt raw file bytes that were encrypted with encryptFileBytes /
 * encryptFileBytesAsync.
 * Returns the original Uint8Array, or null if the key is wrong or data is
 * corrupt.
 *
 * For payloads > 1 MB the decryption runs in a Web Worker.
 */
export async function decryptFileBytesAsync(
  encryptedB64: string,
  channelKeyB64: string,
): Promise<Uint8Array | null> {
  // Check the encoded size to estimate the decoded size without decoding.
  // Base64 encodes 3 bytes as 4 chars, so decoded ~ encodedLen * 3/4.
  const estimatedSize = Math.ceil(encryptedB64.length * 0.75);

  if (estimatedSize <= WORKER_THRESHOLD || !supportsWorkers()) {
    return decryptFileBytesSync(encryptedB64, channelKeyB64);
  }

  try {
    const response = await postToWorker({ type: 'decrypt', payload: encryptedB64, channelKeyB64 });
    if (response.error || response.result === null) {
      return null;
    }
    return response.result as Uint8Array;
  } catch {
    // Fallback to synchronous on any worker failure.
    return decryptFileBytesSync(encryptedB64, channelKeyB64);
  }
}
